// Global overlay for DOM-wide CRT UI frosting: scanlines, screendoor (aperture mask),
// optional vignette + grain. Renders as a transparent WebGL2 canvas over the page.
//
// Engine-agnostic; no coupling to your post stack.
// Named exports, Prettier-friendly. Works on HiDPI and resizes with DPR.
//
// Tested on WebGL2. If WebGL2 is unavailable, throws (fail fast).

export type OverlayParams = {
  scanlinesEnabled: boolean;
  scanlineStrength: number; // 0..1 (darkening)
  scanlineDensity: number; // lines per screen height (e.g., 800..1200)

  screendoorEnabled: boolean;
  screendoorStrength: number; // 0..1

  vignetteEnabled: boolean;
  vignetteStrength: number; // 0..1

  grainEnabled: boolean;
  grainStrength: number; // 0..0.3 typical

  mixBlendMode?: CSSStyleDeclaration["mixBlendMode"]; // 'overlay'|'multiply'|'soft-light'|'screen'...
};

export const defaultOverlayParams: OverlayParams = {
  scanlinesEnabled: false,
  scanlineStrength: 0.6,
  scanlineDensity: 900,

  screendoorEnabled: true,
  screendoorStrength: 0.9,

  vignetteEnabled: true,
  vignetteStrength: 0.75,

  grainEnabled: false,
  grainStrength: 0.2,

  mixBlendMode: "overlay",
};

export class GlobalOverlay {
  private canvas: HTMLCanvasElement;
  private gl: WebGL2RenderingContext;
  private vao: WebGLVertexArrayObject;
  private vbo: WebGLBuffer;
  private program: WebGLProgram;
  private uniforms: Record<string, WebGLUniformLocation>;
  private raf = 0;
  private start = performance.now();
  private params: OverlayParams;

  constructor(params: Partial<OverlayParams> = {}) {
    this.params = { ...defaultOverlayParams, ...params };
    this.canvas = document.createElement("canvas");
    Object.assign(this.canvas.style, {
      position: "fixed",
      inset: "0",
      pointerEvents: "none",
      zIndex: "2147483647",
      mixBlendMode: this.params.mixBlendMode!,
      background: "transparent",
    } as CSSStyleDeclaration);

    const gl = this.canvas.getContext("webgl2", {
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: true,
      desynchronized: true,
      powerPreference: "low-power",
    }) as WebGL2RenderingContext | null;
    if (!gl) throw new Error("WebGL2 context required for GlobalOverlay");
    this.gl = gl;

    // Shaders
    const vs = `#version 300 es
    layout(location=0) in vec2 aPos;
    out vec2 vUV;
    void main() {
      vUV = (aPos + 1.0) * 0.5;
      gl_Position = vec4(aPos, 0.0, 1.0);
    }`;

    const fs = `#version 300 es
    precision highp float;
    in vec2 vUV;
    out vec4 frag;

    uniform vec2 uResolution;
    uniform float uTime;

    uniform bool uScanOn;
    uniform float uScanStrength;
    uniform float uScanDensity;

    uniform bool uMaskOn;
    uniform float uMaskStrength;

    uniform bool uVignetteOn;
    uniform float uVignetteStrength;

    uniform bool uGrainOn;
    uniform float uGrainStrength;

    // Hash-based grain (time-varying, cheap)
    float hash(vec2 p) {
      p = fract(p * vec2(123.34, 456.21));
      p += dot(p, p + 45.32);
      return fract(p.x * p.y);
    }

    float grain(vec2 uv, float t) {
      // Animate slowly to avoid crawling
      vec2 sp = uv * uResolution + vec2(t * 15.73, t * 11.17);
      return hash(floor(sp));
    }

    // Scanlines in screen space
    float scanlines(vec2 uv) {
      // uv.y * density gives lines per screen height
      float y = uv.y * uScanDensity;
      float s = 0.5 + 0.5 * cos(3.14159265 * y);
      // invert to make dark lines (valleys)
      float shaped = pow(s, 4.0);
      return mix(1.0, shaped, uScanStrength);
    }

    // Aperture grille / screendoor RGB stripe
    vec3 apertureMask(vec2 uv) {
      if (!uMaskOn) return vec3(1.0);
      float x = floor(uv.x * uResolution.x);
      float stripe = mod(x, 3.0);
      vec3 m;
      if (stripe < 0.5) m = vec3(1.0, 0.6, 0.6);
      else if (stripe < 1.5) m = vec3(0.6, 1.0, 0.6);
      else m = vec3(0.6, 0.6, 1.0);
      return mix(vec3(1.0), m, uMaskStrength);
    }

    // Gentle vignette
    float vignette(vec2 uv) {
      if (!uVignetteOn) return 1.0;
      vec2 d = uv - 0.5;
      float r = dot(d, d);
      float v = smoothstep(0.75, 0.1, r);
      return mix(1.0, v, uVignetteStrength);
    }

    void main() {
      vec2 uv = vUV;
      vec3 color = vec3(1.0);
      float alpha = 0.0;

      // Base modulation from scanlines (multiplies underlying content via blend-mode)
      float scan = uScanOn ? scanlines(uv) : 1.0;
      color *= scan;
      alpha = max(alpha, uScanOn ? uScanStrength * 0.9 : 0.0);

      // Screendoor mask
      vec3 mask = apertureMask(uv);
      color *= mask;
      alpha = max(alpha, uMaskOn ? uMaskStrength * 1.0 : 0.0);

      // Vignette (darken corners)
      float vig = vignette(uv);
      color *= vig;
      alpha = max(alpha, uVignetteOn ? uVignetteStrength * 1.0 : 0.0);

      // Grain (additive, subtle)
      if (uGrainOn) {
        float g = grain(uv, uTime * 0.025) * 2.0 - 1.0;
        color += g * uGrainStrength;
        alpha = max(alpha, uGrainStrength * 1.0);
      }

      // Premultiplied alpha output (color already carries intensity)
      frag = vec4(color * alpha, alpha);
    }`;

    const compile = (type: number, src: string) => {
      const s = this.gl.createShader(type)!;
      this.gl.shaderSource(s, src);
      this.gl.compileShader(s);
      if (!this.gl.getShaderParameter(s, this.gl.COMPILE_STATUS)) {
        throw new Error(this.gl.getShaderInfoLog(s) || "Shader compile failed");
      }
      return s;
    };

    const prog = this.gl.createProgram()!;
    this.gl.attachShader(prog, compile(this.gl.VERTEX_SHADER, vs));
    this.gl.attachShader(prog, compile(this.gl.FRAGMENT_SHADER, fs));
    this.gl.linkProgram(prog);
    if (!this.gl.getProgramParameter(prog, this.gl.LINK_STATUS)) {
      throw new Error(this.gl.getProgramInfoLog(prog) || "Program link failed");
    }
    this.program = prog;

    // Cache uniform locations for performance
    const uniformNames = [
      "uResolution",
      "uTime",
      "uScanOn",
      "uScanStrength",
      "uScanDensity",
      "uMaskOn",
      "uMaskStrength",
      "uVignetteOn",
      "uVignetteStrength",
      "uGrainOn",
      "uGrainStrength",
    ];
    this.uniforms = {};
    for (const name of uniformNames) {
      const loc = this.gl.getUniformLocation(prog, name);
      if (loc) this.uniforms[name] = loc;
    }

    // Fullscreen quad
    const vao = this.gl.createVertexArray()!;
    this.gl.bindVertexArray(vao);
    const vbo = this.gl.createBuffer()!;
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, vbo);
    this.gl.bufferData(
      this.gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      this.gl.STATIC_DRAW,
    );
    this.gl.enableVertexAttribArray(0);
    this.gl.vertexAttribPointer(0, 2, this.gl.FLOAT, false, 0, 0);
    this.vao = vao;
    this.vbo = vbo;

    // GL state
    this.gl.disable(this.gl.DEPTH_TEST);
    this.gl.enable(this.gl.BLEND);
    // Premultiplied alpha blending
    this.gl.blendFuncSeparate(
      this.gl.ONE,
      this.gl.ONE_MINUS_SRC_ALPHA,
      this.gl.ONE,
      this.gl.ONE_MINUS_SRC_ALPHA,
    );

    this.resize();
    addEventListener("resize", this.resize, { passive: true });
  }

  mount(parent: HTMLElement = document.body) {
    // Ensure it's above everything without affecting layout or input
    parent.appendChild(this.canvas);
    this.loop();
  }

  update(params: Partial<OverlayParams>) {
    Object.assign(this.params, params);
    if (params.mixBlendMode)
      this.canvas.style.mixBlendMode = params.mixBlendMode;
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    removeEventListener("resize", this.resize);

    // Clean up WebGL resources
    const gl = this.gl;
    gl.deleteProgram(this.program);
    gl.deleteVertexArray(this.vao);
    gl.deleteBuffer(this.vbo);

    this.canvas.remove();
  }

  // --- internals ---
  private loop = () => {
    const t = (performance.now() - this.start) / 1000;
    const gl = this.gl;
    const p = this.params;

    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);

    // Use cached uniform locations for performance
    const u = this.uniforms;
    gl.uniform2f(u.uResolution!, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.uniform1f(u.uTime!, t);

    gl.uniform1i(u.uScanOn!, p.scanlinesEnabled ? 1 : 0);
    gl.uniform1f(u.uScanStrength!, p.scanlineStrength);
    gl.uniform1f(u.uScanDensity!, p.scanlineDensity);

    gl.uniform1i(u.uMaskOn!, p.screendoorEnabled ? 1 : 0);
    gl.uniform1f(u.uMaskStrength!, p.screendoorStrength);

    gl.uniform1i(u.uVignetteOn!, p.vignetteEnabled ? 1 : 0);
    gl.uniform1f(u.uVignetteStrength!, p.vignetteStrength);

    gl.uniform1i(u.uGrainOn!, p.grainEnabled ? 1 : 0);
    gl.uniform1f(u.uGrainStrength!, p.grainStrength);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
    this.raf = requestAnimationFrame(this.loop);
  };

  private resize = () => {
    const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    const w = Math.floor(innerWidth * dpr);
    const h = Math.floor(innerHeight * dpr);
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    // Keep CSS size in CSS pixels to match the page
    this.canvas.style.width = "100vw";
    this.canvas.style.height = "100vh";
  };
}
